import type { ConfigGoal } from '@springboard/shared';
import type { Tracker } from './tracker';
import { globToRegex } from './util';

/**
 * Goal detection on the host page (planning.md FR-W-15). Never reads form field values.
 *  - url_match: the current path matches pathPattern on page view.
 *  - form_submit: a submit event on a form whose page path matches pathPattern, or form matches selector;
 *    also a click on a button inside such a form (JS-submitted forms never fire submit).
 *  - cta_click: a click on an anchor whose href matches urlPattern, or element matches selector.
 */
export function installGoalDetection(goals: ConfigGoal[], tracker: Tracker): void {
  const path = location.pathname;
  const here = location.href;
  const hits = (pattern: string | undefined, ...candidates: string[]) => !!pattern && candidates.some((c) => globToRegex(pattern).test(c));
  for (const g of goals) {
    // url_match: pathPattern against the path, or urlPattern against the full URL (the scan proposes either).
    if (g.type === 'url_match' && (hits(g.match.pathPattern, path) || hits(g.match.urlPattern, here, path))) tracker.conversion(g.id);
  }

  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target as HTMLFormElement | null;
      if (!form || form.tagName !== 'FORM') return;
      for (const g of goals) {
        if (g.type !== 'form_submit') continue;
        const bySelector = g.match.selector ? safeMatches(form, g.match.selector) : false;
        const byPath = g.match.pathPattern ? globToRegex(g.match.pathPattern).test(path) : false;
        if (bySelector || byPath || (!g.match.selector && !g.match.pathPattern)) tracker.conversion(g.id);
      }
    },
    true,
  );

  document.addEventListener(
    'click',
    (e) => {
      const target = e.target as Element | null;
      const a = target?.closest?.('a[href]') as HTMLAnchorElement | null;
      // Many sites send forms from a type="button" handler, which never fires a submit event. A click on a button
      // inside a form that a form_submit goal targets counts as the submission attempt.
      const button = target?.closest?.('form button, form input[type=submit], form input[type=button]');
      const form = button?.closest('form');
      if (form) {
        for (const g of goals) {
          if (g.type !== 'form_submit') continue;
          const bySelector = g.match.selector ? safeMatches(form, g.match.selector) || !!safeClosest(form, g.match.selector) : false;
          const byPath = g.match.pathPattern ? globToRegex(g.match.pathPattern).test(path) : false;
          if (bySelector || byPath || (!g.match.selector && !g.match.pathPattern)) tracker.conversion(g.id);
        }
      }
      for (const g of goals) {
        if (g.type !== 'cta_click') continue;
        const bySelector = g.match.selector && target ? !!safeClosest(target, g.match.selector) : false;
        // Match the raw href, the resolved URL, or the resolved path against either pattern kind.
        const byUrl = a ? hits(g.match.urlPattern, a.getAttribute('href') || '', a.href, a.pathname) || hits(g.match.pathPattern, a.pathname) : false;
        if (bySelector || byUrl) tracker.conversion(g.id);
      }
    },
    true,
  );
}

function safeClosest(el: Element, selector: string): Element | null {
  try {
    return el.closest(selector);
  } catch {
    return null;
  }
}

function safeMatches(el: Element, selector: string): boolean {
  try {
    return el.matches(selector);
  } catch {
    return false;
  }
}
